import * as arrow from "apache-arrow";
import { convertValueToType } from "../../utils/parquet";

export interface SupplyWalkSegmentationCombination {
  segmentValues: Record<string, string>;
  segmentationRank: number;
  displayKey: string;
  rowIndices: number[];
}

const normalizeSegmentValue = (value: unknown): string =>
  value == null || value === "" ? "" : String(value).trim();

const readValue = (
  table: arrow.Table,
  fields: Map<string, arrow.Field<any>>,
  name: string,
  rowIndex: number,
): unknown => {
  const rawValue = table.getChild(name)?.get(rowIndex) ?? null;
  const field = fields.get(name);
  return rawValue != null && field
    ? convertValueToType(rawValue, field.type)
    : rawValue;
};

export const buildSupplyWalkSegmentationIndex = ({
  table,
  columns,
  segments,
}: {
  table: arrow.Table;
  columns: arrow.Field<any>[];
  segments: string[];
}): SupplyWalkSegmentationCombination[] => {
  const fields = new Map(columns.map((column) => [column.name, column]));
  const hasSegmentationColumn = fields.has("segmentation");
  const segmentColumnsMissing = segments.some(
    (segment) => !fields.has(segment),
  );
  const combinations = new Map<string, SupplyWalkSegmentationCombination>();

  for (let rowIndex = 0; rowIndex < table.numRows; rowIndex += 1) {
    const segmentValues: Record<string, string> = {};

    if (hasSegmentationColumn && segmentColumnsMissing) {
      const combined = normalizeSegmentValue(
        readValue(table, fields, "segmentation", rowIndex),
      ).split(" | ");
      segments.forEach((segment, index) => {
        segmentValues[segment] = combined[index] ?? "";
      });
    } else {
      segments.forEach((segment) => {
        segmentValues[segment] = normalizeSegmentValue(
          readValue(table, fields, segment, rowIndex),
        );
      });
    }

    const displayKey = segments
      .map((segment) => segmentValues[segment])
      .join(" | ");
    const existing = combinations.get(displayKey);
    if (existing) {
      existing.rowIndices.push(rowIndex);
      continue;
    }

    const rankValue =
      readValue(table, fields, "segmentation_rank", rowIndex) ||
      readValue(table, fields, "segmentationRank", rowIndex) ||
      0;
    combinations.set(displayKey, {
      segmentValues,
      segmentationRank: Number(rankValue) || 0,
      displayKey,
      rowIndices: [rowIndex],
    });
  }

  return Array.from(combinations.values()).sort(
    (left, right) => left.segmentationRank - right.segmentationRank,
  );
};
